import clsx from 'clsx'
import {
  getInitials,
  getTenantIconLogo,
  getTenantLogo,
  isSelectiveAiDomains,
} from 'common/HelperFunctions'
import {CSSProperties, FC, ImgHTMLAttributes, useEffect, useState} from 'react'
import {shallowEqual, useDispatch} from 'react-redux'
import {useSelector} from 'react-redux'
import {MainReducer, RootState} from 'setup'
import {getBranchLogo} from 'setup/redux/Actions/Dashboard/dashboard'

import {toAbsoluteUrl} from '_metronic/helpers'
import {getTheme, useThemeMode} from '_metronic/partials'

type Size = 'big' | 'small'

/** `src` is resolved from tenant settings, so accepting one from a caller would be misleading. */
type Props = {
  size?: Size
  showBranchLogo?: boolean
  /** When `sidebar`, renders expanded + minimized logos for Metronic sidebar toggle. */
  variant?: 'default' | 'sidebar'
} & Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'>

const SELECTIVE_AI_LOGO = 'media/logos/da1timetable.png'

/**
 * The sidebar and header mount two to three <Logo>s at once and the endpoint takes no
 * parameters, so concurrent callers share one in-flight request. Resolved values are never
 * cached -- the key changes with `generalSettings`, so a branch switch still refetches.
 */
let pendingBranchLogo: {key: unknown; promise: Promise<string | null>} | null = null

const fetchBranchLogo = (dispatch: (action: any) => void, key: unknown): Promise<string | null> => {
  if (pendingBranchLogo && pendingBranchLogo.key === key) return pendingBranchLogo.promise

  const promise = new Promise<string | null>((resolve) => {
    dispatch(
      getBranchLogo(
        (res: any) => resolve(res?.result?.branch_logo ?? null),
        () => resolve(null)
      )
    )
  }).finally(() => {
    if (pendingBranchLogo?.promise === promise) pendingBranchLogo = null
  })

  pendingBranchLogo = {key, promise}
  return promise
}

/**
 * `hasTenantLogo` is false only when no custom image, no settings logo and no bundled logo
 * resolved -- i.e. the logo on screen came from the branch API.
 */
const logoSizeClass = (size: Size, hasTenantLogo: boolean) =>
  clsx({
    'h-80px': size === 'big' && hasTenantLogo,
    'h-55px': size === 'small' && !hasTenantLogo,
    'h-40px': size === 'small' && hasTenantLogo,
  })

const SelectiveAiLogo: FC<{
  size: Size
  alt: string
  className?: string
  variant: 'default' | 'sidebar'
  mode: string
}> = ({size, alt, className, variant, mode}) => {
  const activeTheme = mode === 'system' ? getTheme() : mode
  const logoStyle = activeTheme === 'dark' ? undefined : {filter: 'brightness(0) saturate(100%)'}
  const heightClass = clsx({'h-80px': size === 'big', 'h-40px': size === 'small'})

  if (variant === 'sidebar') {
    const minimizeStyle: CSSProperties = {
      ...logoStyle,
      objectFit: 'contain',
      maxHeight: '32px',
      width: 'auto',
      height: 'auto',
      maxWidth: '100%',
    }

    return (
      <>
        <div className='d-flex align-items-center gap-2 app-sidebar-minimize-d-none'>
          <img
            alt={alt}
            className={clsx(heightClass, 'w-auto', className, 'app-sidebar-logo-default')}
            src={toAbsoluteUrl(SELECTIVE_AI_LOGO)}
            style={logoStyle}
          />
          <span className='fw-bold fs-2 text-white-dark'>Timetable™</span>
        </div>
        <img
          alt={alt}
          className='app-sidebar-logo-minimize selective-ai-sidebar-logo-minimize shrink-0'
          src={toAbsoluteUrl(SELECTIVE_AI_LOGO)}
          style={minimizeStyle}
        />
      </>
    )
  }

  return (
    <div className='d-flex align-items-center gap-2'>
      <img
        alt={alt}
        className={clsx(heightClass, className)}
        src={toAbsoluteUrl(SELECTIVE_AI_LOGO)}
        style={logoStyle}
      />
      <span className='fw-bold fs-2 text-white-dark app-sidebar-minimize-d-none'>Timetable™</span>
    </div>
  )
}

const Logo: FC<Props> = ({
  size = 'big',
  alt,
  className,
  showBranchLogo = true,
  variant = 'default',
  ...remainingProps
}) => {
  const dispatch = useDispatch()

  const generalSettings = useSelector<RootState>(
    ({mainReducer}) => mainReducer.generalSettings,
    shallowEqual
  ) as MainReducer['generalSettings']

  const {mode} = useThemeMode()

  const [branchLogo, setBranchLogo] = useState<string | null>(null)
  const [branchLogoResolved, setBranchLogoResolved] = useState(!showBranchLogo)
  const [brokenSrcs, setBrokenSrcs] = useState<string[]>([])

  useEffect(() => {
    if (!showBranchLogo) {
      setBranchLogo(null)
      setBranchLogoResolved(true)
      return
    }

    // Superseded requests must not overwrite a newer one when they land out of order.
    let ignore = false
    setBranchLogoResolved(false)

    fetchBranchLogo(dispatch, generalSettings).then((logo) => {
      if (ignore) return
      setBranchLogo(logo)
      setBranchLogoResolved(true)
    })

    return () => {
      ignore = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBranchLogo, generalSettings])

  const altText = alt ?? 'Logo'

  if (isSelectiveAiDomains()) {
    return (
      <SelectiveAiLogo
        size={size}
        alt={altText}
        className={className}
        variant={variant}
        mode={mode}
      />
    )
  }

  // Read on every render rather than memoized: getTenantLogo reads the redux store and the
  // active theme directly, so a dependency array could not describe what it actually depends on.
  const settingsLogo = getTenantLogo(size) ?? null
  const hasTenantLogo = !!settingsLogo

  // Branch logo wins when present; a broken one falls through to the next candidate. On
  // white-labelled builds getTenantLogo yields nothing, so the chain ends at the text fallback
  // and no ilmversity asset is ever reachable from here.
  const candidates = [showBranchLogo ? branchLogo : null, settingsLogo].filter(
    (candidate): candidate is string => !!candidate && !brokenSrcs.includes(candidate)
  )
  const logoSrc = candidates[0] ?? null

  const markBroken = (src: string) =>
    setBrokenSrcs((previous) => (previous.includes(src) ? previous : [...previous, src]))

  const tenantName =
    typeof generalSettings?.tenantName === 'string' ? generalSettings.tenantName.trim() : ''

  if (!branchLogoResolved) {
    return null
  }

  if (!logoSrc) {
    if (!tenantName) return null

    const nameClass = clsx('fw-bold text-nowrap', size === 'big' ? 'fs-1' : 'fs-4', className)

    if (variant === 'sidebar') {
      // The sidebar logo strip has a fixed height, so long names clamp to two lines
      // (app-sidebar-tenant-name) instead of nowrap-overflowing past the sidebar edge.
      return (
        <>
          <span
            className={clsx(
              'fw-bold fs-4 app-sidebar-logo-default app-sidebar-tenant-name',
              className
            )}
            title={tenantName}
          >
            {tenantName}
          </span>
          <span className={clsx('fw-bold fs-4 app-sidebar-logo-minimize')}>
            {getInitials(tenantName)}
          </span>
        </>
      )
    }

    return <span className={nameClass}>{tenantName}</span>
  }

  if (variant === 'sidebar') {
    // Collapsed state shows the same tenant/branch logo as expanded; the ilmversity icon is
    // only a last resort when no tenant logo resolved at all.
    const iconLogo = logoSrc ?? getTenantIconLogo()

    return (
      <>
        <img
          alt={altText}
          src={logoSrc}
          className={clsx(
            logoSizeClass(size, hasTenantLogo),
            'app-sidebar-logo-default',
            className
          )}
          onError={() => markBroken(logoSrc)}
          {...remainingProps}
        />
        {iconLogo && (
          <img
            alt={altText}
            src={iconLogo}
            className='h-25px w-25px app-sidebar-logo-minimize'
            style={{objectFit: 'contain'}}
            onError={() => markBroken(iconLogo)}
          />
        )}
      </>
    )
  }

  return (
    <img
      alt={altText}
      src={logoSrc}
      className={clsx(logoSizeClass(size, hasTenantLogo), className)}
      onError={() => markBroken(logoSrc)}
      {...remainingProps}
    />
  )
}

export default Logo
